import type { Arenda } from '../aktualnost/api';
import type { Liniya } from './components/VyborLiniy';
import type { Proekt } from './components/VyborProekta';
import { apiClient, ensureCsrfCookie } from '../../api/client';
import type { Hints, ScreenWords } from '../clients/api';

/**
 * ЭКРАН «ОБРАЩЕНИЯ» — очередь дежурного (`s603`).
 *
 * ⛔ НИ ОДНОГО РАСЧЁТА НА ФРОНТЕ. «Ждёт N мин», срочность, счётчики вкладок и
 * цвет филиала приезжают ПОСЧИТАННЫМИ (закон `s104`): те же величины показывает
 * бар в шапке, и посчитай их здесь — два числа разошлись бы молча.
 */

/**
 * ⛔ «Активные» — свод «всё, что требует поступка», а не пятая стопка. Считать
 * её сложением соседей нельзя: она с ними намеренно пересекается.
 */
export type Vkladka = 'aktivnye' | 'novye' | 'v_rabote' | 'bez_klientki' | 'razobrannye';

export type Srochnost = 'net' | 'obychno' | 'preduprezhdenie' | 'opasno';

export interface FilialPlashka {
  id: number | null;
  name: string;
  /** Короткое имя для плашки — свойство ФИЛИАЛА, а не обрезка на экране (п.3). */
  short_name: string;
  color: string;
}

export interface StrokaOcheredi {
  id: number;
  sostoyanie: string;
  sostoyanie_display: string;
  kanal: { kind: string; kind_display: string; title: string };
  filial: FilialPlashka;
  customer_id: number | null;
  /** Имя клиентки или `null` — тогда экран говорит «Кто это?». */
  customer_display: string | null;
  est_zayavka: boolean;
  /**
   * Три вида «ждёт» (`s605`, п.6): `ne_prochitano` · `zhdyot_otveta` ·
   * `otlozheno` · `tishina`. Считает сервер одним источником — фронт не
   * выводит вид сам ни из каких полей, иначе завёлся бы четвёртый счёт.
   */
  vid_ozhidaniya: VidOzhidaniya;
  vid_ozhidaniya_display: string;
  otlozheno_do: string | null;
  otlozheno_prichina_display: string | null;
  zhdyot_minut: number | null;
  /** Готовая фраза ожидания — собрана сервером, чтобы шапка и строка не разошлись. */
  zhdyot_display: string | null;
  /** Та же величина без слова «ждёт» — для строки, где вид уже назван. */
  zhdyot_skolko: string | null;
  srochnost: Srochnost;
  derzhit: string | null;
  derzhit_user_id: number | null;
  /** `s731`: переписку можно закрыть прямо из списка — решает сервер (открыта и не лид). Прежний сервер поля не шлёт. */
  mozhno_zakryt?: boolean;
  preview: string;
  preview_nashe: boolean;
  /** ВИД доставки последнего сообщения — тот же, что в ленте (`s608`). */
  preview_dostavka: string | null;
  neprochitannyh: number;
  talk_id: number | null;
  /**
   * Время последнего сообщения беседы, Telegram-стиль: сегодня «чч:мм»,
   * раньше — короткой датой. Тот же источник, что у сортировки списка.
   */
  poslednee_soobshchenie_vremya: string | null;
}

export type VidOzhidaniya = 'ne_prochitano' | 'zhdyot_otveta' | 'otlozheno' | 'tishina' | 'pora_perezvonit';

/** Бар шапки: три числа одного источника (`s605`, п.6). */
export interface BarSvodka {
  /** Сумма трёх видов. Оставлено ради совместимости — глаз читает числа ниже. */
  novyh: number;
  ne_prochitano: number;
  zhdut_otveta: number;
  otlozheno: number;
  samoe_staroe_minut: number | null;
  srochnost: Srochnost;
  porogi: { preduprezhdenie: number; opasno: number };
  provereno_v: string;
  podpis: string;
  skolko_zhdut: string;
  samoe_staroe_display: string | null;
  vkladka: Vkladka;
  podpis_korotko: string;
  samoe_staroe_id?: number | null;
  samoe_staroe_podpis?: string | null;
  /** Вид ожидания самого долгого диалога: ссылка «самое долгое» открывает его в точной выборке. */
  samoe_staroe_vid?: VidOzhidaniya | null;
  pora_perezvonit?: number;
  uvedomleniya?: Array<{ vid: VidOzhidaniya; count: number; slovo: string }>;
  slova?: Record<string, string>;
}

export interface OcheredOtvet {
  /** `s635`: выбранный источник и список источников словами сервера. */
  istochnik?: string | null;
  istochniki?: Array<{ kod: string; slovo: string }>;
  /** `[s635-filtr-linii]`: линии (каналы) словами сервера и выбранные. */
  linii?: Liniya[];
  vybrany_linii?: number[];
  /** `s675`: проекты филиала шапки (0 — «Весь филиал»); пусто — фильтра нет. */
  pagination?: { stranica: number; na_stranice: number; vsego: number; est_esche: boolean; sleduyushchaya: number | null };
  proekty?: Proekt[];
  vybrany_proekty?: number[];
  vkladka: Vkladka;
  /** Вкладку выбрал сервер, потому что экран её не назвал (первое открытие). */
  vkladka_vybral_server: boolean;
  filial_id: number | null;
  poisk: string | null;
  filialy: FilialPlashka[];
  schyotchiki: Record<Vkladka, number>;
  /** Разбор каждой вкладки по трём видам «ждёт» — тем же условием, что строки. */
  troyka_vkladok: Record<Vkladka, Record<'ne_prochitano' | 'zhdyot_otveta' | 'otlozheno', number>>;
  /**
   * ⛔ БАР ПРИЕЗЖАЕТ ВМЕСТЕ С ОЧЕРЕДЬЮ (`s605`). Он живёт в общей шапке и
   * спрашивается своим запросом, но когда экран очереди открыт, оба ответа
   * относятся к одному мигу и обязаны сойтись. Владелец увидел именно
   * расхождение на такт: «тут уже прочитал а сверху еще висит непрочитанное».
   */
  bar: BarSvodka;
  porogi: { preduprezhdenie: number; opasno: number };
  stroki: StrokaOcheredi[];
  provereno_v: string;
  /** Сколько ждут ответа ВО ВСЕЙ очереди — чтобы пустая вкладка не врала. */
  zhdut_vsego: number;
  zhdut_vkladka: Vkladka;
  zhdut_display: string | null;
  /** Каналы, чьи входящие пока идут в amoCRM, а не сюда (п.14). */
  kanaly_cherez_amo: { code: string; kind: string; kind_display: string }[];
  hints: Hints;
  words: ScreenWords;
}

export interface VlozhenieLenty {
  vid: string;
  vid_display: string;
  imya: string;
  mime: string;
  bayt: number | null;
  /** Файл лежит у нас. `false` — забрать со ссылки провайдера не успели. */
  est_u_nas: boolean;
  /** Наша дверь с правом очереди, а не адрес провайдера. */
  adres: string | null;
  /** Почему файла нет — словом словаря («файл не сохранился у amo»), `s619`. */
  sostoyanie_display?: string | null;
}

export interface GolosLenty {
  /** `vsem` · `po_knopke` · `net` — настройка филиала. */
  rezhim: string;
  tekst: string | null;
  sostoyanie: string;
  mozhno: boolean;
}

/**
 * `s713`: подпись строки — кто написал и через какую НАШУ линию прошло именно это сообщение.
 * Собирает сервер по самой строке; экран не подставляет выбранную сейчас линию ответа.
 */
export interface PodpisStrokiLenty {
  kto: string;
  /** `s720`: аккаунт клиентки в мессенджере у входящего (номер краями, ник); совпал с именем — `null`. */
  akkaunt?: string | null;
  /** «Получено на» у входящего, «Отправлено с» у исходящего. */
  liniya_slovo: string;
  liniya: string | null;
}

/** Одинаковая подпись у соседних строк пишется один раз — как имя в групповом чате. */
export function klyuchPodpisi(s: { nashe: boolean | null; podpis?: PodpisStrokiLenty | null }): string {
  return s.podpis == null ? "" : `${s.nashe ? 1 : 0}|${s.podpis.kto}|${s.podpis.liniya ?? ""}`;
}

/**
 * `s711` (Д-18): что о доставке известно на самом деле. «Отправлено» канала, который о
 * доставке не сообщает, — не «доставлено».
 */
export interface FaktDostavki {
  sostoyanie: 'pending' | 'sent' | 'delivered' | 'read' | 'error' | string;
  dostavka: 'dostavleno' | 'zhdyom' | 'neizvestno' | 'ne_dostavleno' | string;
  podtverzhdeno?: boolean;
  kanal_soobshchaet?: boolean;
  slovo: string | null;
  kogda?: string | null;
  oshibka?: { kod?: string | null; slovo?: string | null } | null;
}

/** Вид значка доставки — от ФАКТА, если сервер его прислал; иначе прежний вид (`s608`). */
export function vidDostavki(s: { dostavka: string | null; dostavka_fakt?: FaktDostavki | null }): string | null {
  const f = s.dostavka_fakt;
  if (f == null) return s.dostavka;

  switch (f.sostoyanie) {
    case 'pending': return 'zhdyom';
    // Мессенджер принял, а дошло ли — не подтверждено: одна галочка, не две.
    case 'sent': return 'ushlo';
    case 'delivered': return 'dostavleno';
    case 'read': return 'prochitano';
    case 'error': return 'ne_ushlo';
    default: return s.dostavka;
  }
}

export interface SoobshchenieLenty {
  id: number;
  podpis?: PodpisStrokiLenty | null;
  dostavka_fakt?: FaktDostavki | null;
  /** `s677`: служебная строка ленты — «zapis»: «Записана на 28.09 12:00 к … — …». */
  sobytie?: string | null;
  nashe: boolean;
  avtor: string;
  avtor_imya: string | null;
  tekst: string;
  vremya: string;
  data: string;
  /**
   * ВИД доставки, посчитанный сервером: `zhdyom` · `ushlo` · `dostavleno` ·
   * `prochitano` · `ne_ushlo`.
   *
   * ⛔ Не код статуса. Один и тот же код значит разное на разных каналах: у
   * Telegram «мессенджер принял» — конец лестницы (событий доставки Bot API не
   * шлёт вовсе), у официального WhatsApp — её середина. Разницу знает
   * провайдер, поэтому считает сервер (`s608`, закон `s104`).
   */
  dostavka: string | null;
  /** Почему не ушло — готовой фразой салона с сервера; `null` у ушедших. */
  dostavka_prichina?: string | null;
  /**
   * Не `null` — это рассылка по согласованному шаблону, а не слова человека
   * (владелец, п.9: «где шаблон от триггера рассылки где что»). Само слово
   * приезжает готовым из `ui_terms`, а не собирается здесь.
   */
  po_shablonu: string | null;
  vlozhenie: VlozhenieLenty | null;
  golos: GolosLenty | null;
}

/**
 * Строка сквозной хроники (`s607`, Ф3) — сообщение, заявка или звонок.
 * У не-сообщения полей пузыря нет: они честно `null`.
 */
export interface StrokaHroniki {
  /** `s677`: «zapis» — запись, заведённая из переписки. */
  tip: "soobshchenie" | "zayavka" | "zvonok" | "zapis";
  id: number | null;
  vremya: string;
  data: string;
  kanal_kind: string;
  kanal_kind_display: string;
  tekst: string;
  nashe: boolean | null;
  avtor: string | null;
  avtor_imya: string | null;
  podpis?: PodpisStrokiLenty | null;
  dostavka_fakt?: FaktDostavki | null;
  po_shablonu: string | null;
  dostavka: string | null;
  dostavka_prichina?: string | null;
  vlozhenie: VlozhenieLenty | null;
  golos: GolosLenty | null;
}

/**
 * Порция хроники клиентки (`s619`): свежие строки и курсор к более ранним.
 * Одна форма на разбор обращения, лайт, окно переписки записи и карточку.
 */
export interface HronikaPorciya {
  stroki: StrokaHroniki[];
  /** Сколько строк у человека всего — сообщения, заявки, звонки. */
  vsego: number;
  est_esche: boolean;
  /** Курсор к более ранним; `null` — раньше ничего нет. */
  kursor: string | null;
}

/** Согласованный шаблон провайдера ЛИБО наша заготовка — вид один, суть разная. */
export interface ShablonStroka {
  /** У согласованного — код провайдера (строка), у заготовки — наш номер. */
  id: string | number;
  nazvanie: string;
  tekst: string;
  /** Только у согласованных: «служебный» / «рекламный». */
  naznachenie?: string;
  /**
   * `s705`: у шаблона с переменными сервер уже подставил значения из нашей базы; если чего-то
   * нет (будущей записи, ссылки оплаты) — здесь готовая фраза, и шаблон не отправляется.
   */
  ne_hvataet?: string | null;
  /**
   * `s705`: шаблон просит оплату брони, а действующей ссылки ЮKassa по записи ещё нет — при
   * открытии предпросмотра сервер получит или создаст её (`podgotovitShablon`).
   */
  podgotovit?: boolean;
  /** `s705`: касса не ответила — причина в `ne_hvataet`, подготовку можно повторить. */
  mozhno_povtorit?: boolean;
}

/** `s720`: почему список согласованных такой и где шаблоны есть (решает сервер, экран печатает). */
export interface SostoyanieSoglasovannyh {
  kod: 'est' | 'net_perepiski' | 'ne_whatsapp' | 'obychnyy_whatsapp' | 'spravochnik_pust' | string;
  liniya: string | null;
  slovo: string | null;
  drugie_linii: { channel_id: number; podpis: string; shablonov: number }[];
}

export interface ShablonyOtveta {
  soglasovannye: ShablonStroka[];
  soglasovannye_sostoyanie?: SostoyanieSoglasovannyh | null;
  zagotovki: ShablonStroka[];
}

/**
 * `s720`: кому, в какой мессенджер и с какой нашей линии уйдёт ответ — три величины по отдельности.
 * Филиал линии — из настроек канала («Общие линии сети», если линия ничья); филиал клиентки — своим полем.
 */
export interface KudaUydyot {
  komu: { imya: string | null; akkaunt: string | null };
  messendzher: { kind: string; slovo: string } | null;
  liniya: { channel_id: number; adres: string; podpis: string; filial: string } | null;
  filial_klientki: string | null;
}

/** `s711`: итог отправки в ответе двери — был ли запрос повтором и ушло ли сообщение. */
export interface ItogOtpravki {
  namerenie?: { povtor?: boolean; bez_klyucha?: boolean; slovo?: string | null } | null;
  message_id?: number | null;
  ushlo?: boolean;
}

export interface NashMessendzher {
  channel_id: number;
  kind: string;
  label: string;
  short_url: string;
  can_sms: boolean;
}

export async function otpravitSsylkuSms(inquiryId: number, channelId: number, intent: string): Promise<{ ok: boolean; id: number }> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/sms/messenger/${inquiryId}/${channelId}`, { intent });
  return data;
}

export interface Razbor {
  nashi_messendzhery?: NashMessendzher[];
  id: number;
  /** `s731`: номер открытого обращения той же беседы, если этот диалог закрыт, а переписка открыта заново. */
  otkryta_zanovo?: number | null;
  otpravka?: ItogOtpravki | null;
  kuda_uydyot?: KudaUydyot | null;
  /** `s712`: отпечаток состояния лида и аренда редактирования (если сервер их шлёт). */
  versiya?: string;
  arenda?: Arenda | null;
  sostoyanie: string;
  sostoyanie_display: string;
  ishod: string | null;
  ishod_display: string | null;
  prichina_otkaza_display: string | null;
  shapka: {
    kanal_kind: string;
    kanal_kind_display: string;
    filial: FilialPlashka;
    customer_id: number | null;
    customer_display: string | null;
    telefon: string | null;
    vizitov: number | null;
    posledniy_vizit: string | null;
    /** `s677`: клиентки нет в базе — номер собеседника краями (нет номера у переписки — пусто). */
    nomer_bez_klientki?: string | null;
  };
  otvet_uydyot_ot: {
    filial: string;
    color: string;
    kanal_kind: string;
    kanal_title: string;
    kanal_podpis?: string;
    adres: string;
  };
  lenta: SoobshchenieLenty[];
  hody: {
    beru: boolean;
    zapisala: boolean;
    razobrano: boolean;
    otbrosit: boolean;
    eto_klientka: boolean;
    novaya_klientka: boolean;
    /** Единственный ход по РАЗОБРАННОМУ обращению (владелец, п.5). */
    vernut: boolean;
    /** Третий вид «ждёт» (`s605`, п.6). */
    otlozhit: boolean;
    vernut_iz_otlozhennyh: boolean;
    derzhit_drugoy: boolean;
    derzhit_imya: string | null;
    okno: { svoimi_slovami: boolean; tolko_shablonom: boolean; chasov?: number };
  };
  prichiny_otkaza: { code: string; label: string }[];
  prichiny_otkladyvaniya: { code: string; label: string }[];
  /** Пока обращение отложено — чем и до какого часа. */
  otlozheno: {
    do: string;
    do_display: string;
    prichina: string;
    prichina_display: string;
    zametka: string | null;
  } | null;
  eyo_messendzhery: {
    kind: string;
    /**
     * Подпись пилюли. У повторяющегося вида (два наших номера WhatsApp) сервер
     * дописывает сюда НОМЕР — `s614`, владелец 18.09.2026: две немые
     * «WhatsApp» на экране дежурный не мог отличить одну от другой.
     */
    kind_display: string;
    /**
     * Имя мессенджера и наша подпись — ДВЕ величины, считает сервер (`s615`).
     * Лист «Ответить через» рисует их разными строками: сверху «WhatsApp»,
     * снизу наш номер или имя бота. Разбирать `kind_display` по разделителю
     * значило бы гадать по строке, собранной для пилюли ряда.
     */
    nazvanie: string;
    podpis: string;
    /** Номер канала — им пилюля называет себя ходу «написать сюда». */
    channel_id: number;
    adres: string;
    osnovnoy: boolean;
    /** Тот канал, в котором открыт ЭТОТ разговор: нажимать некуда — уже здесь. */
    eto_tekushchiy: boolean;
    /** `s720`: `false` — беседы на этой линии ещё нет, её откроет выбор. Прежний сервер поля не шлёт. */
    est_perepiska?: boolean;
    /** `s720`: чья линия по настройкам — имя филиала либо «Общие линии сети». */
    filial?: string;
  }[];
  /** Сквозная хроника — сообщения всех каналов + заявки + звонки (`s607`, Ф2–Ф4). */
  hronika: HronikaPorciya;
  /** Связанные карточки (объединённые с этой) — для «Разъединить». */
  svyazannye: { id: number; imya: string }[];
  hints: Hints;
  words: ScreenWords;
  ochered?: BarSvodka;
}

export interface BarSvodka {
  novyh: number;
  samoe_staroe_minut: number | null;
  srochnost: Srochnost;
  porogi: { preduprezhdenie: number; opasno: number };
  provereno_v: string;
  /** Готовая фраза бара словами из `ui_terms` — собрана сервером, не здесь. */
  podpis: string;
  /** Верхняя линия бара: «3 новых». Тот же счёт, что и в `podpis`. */
  skolko_zhdut: string;
  /** Нижняя линия бара: «ждёт 38 мин» — та же фраза, что в строке очереди. */
  samoe_staroe_display: string | null;
  /** Вкладка, где лежит самое старое ждущее обращение: нажатие ведёт ТУДА. */
  vkladka: Vkladka;
  /** Сжатая форма для телефона — из того же счёта, что и полная фраза. */
  podpis_korotko: string;
}

export interface NaydennayaKlientka {
  id: number;
  customer_display: string;
  telefon: string | null;
  vizitov: number;
  posledniy_vizit: string | null;
}

export interface OcheredFiltry {
  /** `null` — «выбери сам»: сервер откроет первую НЕПУСТУЮ (владелец, п.11). */
  vkladka: Vkladka | null;
  filial_id: number | null;
  poisk: string;
  /** `s635`: фильтр источников — «форма · звонок»; `null` — все. */
  istochnik?: string | null;
  /** `[s635-filtr-linii]`: выбранные линии; пусто — все. */
  linii?: number[];
  /** `s675`: выбранные проекты; пусто — все. */
  proekty?: number[];
  vid_ozhidaniya?: VidOzhidaniya;
  neprochitannye?: boolean;
  bez_klientki?: boolean;
  rabochee_sostoyanie?: 'new' | 'in_work';
  stranica?: number;
}

export async function fetchOchered(f: OcheredFiltry): Promise<OcheredOtvet> {
  const { data } = await apiClient.get<OcheredOtvet>('/obrashcheniya', {
    params: {
      vkladka: f.vkladka ?? undefined,
      filial_id: f.filial_id ?? undefined,
      poisk: f.poisk.trim() !== '' ? f.poisk.trim() : undefined,
      istochnik: f.istochnik ?? undefined,
      linii: f.linii !== undefined && f.linii.length > 0 ? f.linii : undefined,
      proekty: f.proekty !== undefined && f.proekty.length > 0 ? f.proekty : undefined,
      vid_ozhidaniya: f.vid_ozhidaniya,
      neprochitannye: f.neprochitannye ? 1 : undefined,
      bez_klientki: f.bez_klientki ? 1 : undefined,
      rabochee_sostoyanie: f.rabochee_sostoyanie,
      stranica: f.stranica ?? undefined,
    },
  });
  return data;
}

export async function fetchBar(filialId: number | null): Promise<BarSvodka> {
  // `s669`: филиал шапки — тот же, что у очереди; `null` — вся сеть.
  const { data } = await apiClient.get<BarSvodka>('/obrashcheniya/bar', {
    params: { filial_id: filialId ?? undefined },
  });
  return data;
}

export async function fetchRazbor(id: number): Promise<Razbor> {
  const { data } = await apiClient.get<Razbor>(`/obrashcheniya/${id}`);
  return data;
}

/** Более ранняя порция хроники клиентки обращения (`s619`). */
export async function fetchHronikaObrashcheniya(id: number, kursor: string | null): Promise<HronikaPorciya> {
  const { data } = await apiClient.get<HronikaPorciya>(`/obrashcheniya/${id}/hronika`, {
    params: kursor !== null ? { do: kursor } : {},
  });
  return data;
}

/** Порция хроники из карточки клиентки (`s619`): та же витрина, свой адрес права. */
export async function fetchHronikaKlientki(id: number, kursor: string | null): Promise<HronikaPorciya> {
  const { data } = await apiClient.get<HronikaPorciya>(`/clients/${id}/hronika`, {
    params: kursor !== null ? { do: kursor } : {},
  });
  return data;
}

export async function poiskKlientki(q: string): Promise<{ najdeno: number; stroki: NaydennayaKlientka[] }> {
  const { data } = await apiClient.get<{ najdeno: number; stroki: NaydennayaKlientka[] }>(
    '/obrashcheniya/poisk-klientki',
    { params: { q } },
  );
  return data;
}

/**
 * «Написать новой» (владелец, 24.09.2026). Шаг 1 — кто это и с каких линий
 * можно написать; сервер ничего не заводит. Шаг 2 — найти или завести клиентку
 * и открыть разговор; ответ — разбор, как у любого хода.
 */
export interface NovayaKto {
  klientka: { imya: string; posledniy_vizit: string | null; perepiska_byla: boolean } | null;
  linii: { channel_id: number; kind: string; title: string }[];
  cherez_amo: boolean;
}

export async function novayaPerepiskaKto(telefon: string): Promise<NovayaKto> {
  const { data } = await apiClient.get<NovayaKto>('/obrashcheniya/novaya-perepiska/kto', {
    params: { telefon },
  });
  return data;
}

export async function novayaPerepiska(telefon: string, channelId: number): Promise<Razbor> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post<Razbor>('/obrashcheniya/novaya-perepiska', {
    telefon,
    channel_id: channelId,
  });
  return data;
}

/** Любой ход возвращает раскрытую строку целиком — экран не дорисовывает сам. */
async function hod(id: number, put: string, telo?: Record<string, unknown>, klyuchNamereniya?: string): Promise<Razbor> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post<Razbor>(`/obrashcheniya/${id}/${put}`, telo ?? {}, zagolovkiNamereniya(klyuchNamereniya));
  return data;
}

/**
 * `s713` по контракту `s711` (Д-19): одно нажатие «Отправить» — один ключ намерения.
 * Сервер по ключу отличает повтор того же нажатия (двойной Enter, обрыв связи) от нового
 * сообщения и второй раз клиентке не шлёт.
 */
function zagolovkiNamereniya(klyuch?: string): { headers?: Record<string, string> } {
  return klyuch === undefined ? {} : { headers: { 'Idempotency-Key': klyuch } };
}

export const vzyat = (id: number) => hod(id, 'beru');
export const razobrano = (id: number, ishod: 'answered' | 'booked') => hod(id, 'razobrano', { ishod });
export const otbrosit = (id: number, prichina: string) => hod(id, 'otbrosit', { prichina });
export const etoKlientka = (id: number, customerId: number) => hod(id, 'klientka', { customer_id: customerId });
export const novayaKlientka = (id: number, imya: string | null) => hod(id, 'novaya-klientka', { imya });
export const otvetit = (id: number, tekst: string, klyuchNamereniya?: string) => hod(id, 'otvet', { tekst }, klyuchNamereniya);
export const prochitano = (id: number) => hod(id, 'prochitano');

/**
 * Приложить файл к ответу (п.6 владельца).
 *
 * ⛔ НЕ ЧЕРЕЗ `hod()`: тот шлёт JSON, а файл уходит `multipart/form-data`.
 * Отправишь файл телом JSON — до сервера доедет имя и ничего больше.
 */
export async function prilozhit(id: number, fayl: File, podpis?: string, klyuchNamereniya?: string): Promise<Razbor> {
  await ensureCsrfCookie();

  const telo = new FormData();
  telo.append('fayl', fayl);

  if (podpis !== undefined && podpis.trim() !== '') {
    telo.append('podpis', podpis.trim());
  }

  const { data } = await apiClient.post<Razbor>(`/obrashcheniya/${id}/vlozhenie`, telo, zagolovkiNamereniya(klyuchNamereniya));

  return data;
}
export const vernut = (id: number) => hod(id, 'vernut');

/**
 * Отложить обращение (`s605`, п.6 владельца).
 *
 * ⛔ ЧАС ВОЗВРАТА ОБЯЗАТЕЛЕН. «Отложить навсегда» — это не отложить, а
 * потерять: строка ушла бы из вида и не вернулась никогда.
 */
export const otlozhit = (id: number, prichina: string, doKogda: string, zametka?: string) =>
  hod(id, 'otlozhit', {
    prichina,
    do_kogda: doKogda,
    ...(zametka !== undefined && zametka.trim() !== '' ? { zametka: zametka.trim() } : {}),
  });

export const vernutIzOtlozhennyh = (id: number) => hod(id, 'vernut-iz-otlozhennyh');

/** Расшифровать ОДНО голосовое строки ленты (п.8). */
export const rasshifrovat = (id: number, stroka: number) => hod(id, `rasshifrovat/${stroka}`);

/**
 * Диктовка дежурного: звук ушёл — текст вернулся (`s616` Ф4).
 *
 * ⛔ БЕЗ НОМЕРА ОБРАЩЕНИЯ. Диктуют в поле ввода, а оно живёт и до выбора
 * обращения; в ленту эта дверь ничего не пишет, значит и привязывать её к
 * записи разговора не к чему.
 *
 * ⛔ `multipart/form-data`, а не JSON: телом JSON до сервера доехало бы имя
 * файла и ничего больше — тот же урок, что у вложений.
 *
 * ⛔ ОТКАЗ ВОЗВРАЩАЕТСЯ СЛОВАМИ СЕРВЕРА. «Не расслышали», «запись слишком
 * длинная» — это ответы человеку, а не коды; показать вместо них «ошибка 422»
 * значило бы заставить дежурного гадать.
 */
export async function nadiktovat(zvuk: Blob): Promise<string> {
  await ensureCsrfCookie();

  const telo = new FormData();
  /*
   * ⛔ ИМЯ ПО ФАКТИЧЕСКОМУ ВИДУ, А НЕ «всегда webm». Запись сводится в WAV ещё
   * в браузере (см. `diktovka.ts`), и назвать её `.webm` значило бы соврать
   * серверу о том, что он получил.
   */
  const rasshirenie = (zvuk.type || '').includes('wav') ? 'wav' : 'webm';
  telo.append('zvuk', zvuk, `diktovka.${rasshirenie}`);

  try {
    const { data } = await apiClient.post<{ tekst: string }>('/obrashcheniya/diktovka', telo);

    return data.tekst;
  } catch (e: unknown) {
    const slova = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;

    throw new Error(slova !== undefined && slova !== '' ? slova : 'rasshifrovka_ne_vyshla', {
      cause: e,
    });
  }
}

/**
 * Что можно подставить в этом обращении (`s606`, Ф3).
 *
 * ⛔ ДВА СПИСКА ПРИЕЗЖАЮТ РАЗДЕЛЬНО, и склеивать их на экране нельзя: по
 * согласованному нажатие ОТПРАВЛЯЕТ, по заготовке — ПОДСТАВЛЯЕТ в поле. Один
 * список значил бы, что дежурный не знает, что сейчас произойдёт.
 */
/** `s720`: одно входящее для уведомления справа внизу. Номер клиентки — только краями. */
export interface VhodyashcheeUvedomlenie {
  id: number;
  obrashchenie_id: number;
  klientka: string;
  filial: string | null;
  kanal: { kind: string; slovo: string };
  liniya: string | null;
  tekst: string | null;
  vlozhenie: { vid: string; slovo: string } | null;
  vremya: string;
  kogda: string;
}

export interface LentaVhodyashchih {
  kursor: number;
  opros_sek: number;
  soobshcheniya: VhodyashcheeUvedomlenie[];
  server_vremya: string;
  /** Слова уведомления (`uvedomlenie.*`) — оно живёт вне экранов со своим словарём. */
  slova?: Record<string, string>;
}

/** Что пришло после отметки. Только чтение: ни «прочитано», ни блокировку эта дверь не ставит. */
export async function fetchVhodyashchie(posle: number | null): Promise<LentaVhodyashchih> {
  const { data } = await apiClient.get<LentaVhodyashchih>('/obrashcheniya/vhodyashchie', {
    params: posle === null ? {} : { posle },
  });

  return data;
}

export async function shablony(id: number): Promise<ShablonyOtveta> {
  const { data } = await apiClient.get<ShablonyOtveta>(`/obrashcheniya/${id}/shablony`);

  return data;
}

/**
 * `s705`: подготовить шаблон к отправке — готовый текст с настоящей ссылкой ЮKassa записи.
 * Ссылка получается или создаётся на сервере; сообщение при этом не отправляется.
 */
export async function podgotovitShablon(id: number, shablon: string): Promise<ShablonStroka> {
  await ensureCsrfCookie();

  const { data } = await apiClient.post<ShablonStroka>(`/obrashcheniya/${id}/shablon/podgotovit`, { shablon });

  return data;
}

/** Отправить СОГЛАСОВАННЫЙ шаблон — он уходит дословно и стоит денег. */
export async function poslatShablon(id: number, shablon: string, klyuchNamereniya?: string): Promise<Razbor> {
  await ensureCsrfCookie();

  const { data } = await apiClient.post<Razbor>(`/obrashcheniya/${id}/shablon`, { shablon }, zagolovkiNamereniya(klyuchNamereniya));

  return data;
}

/**
 * Написать этой же клиентке в другой мессенджер (`s606`, Ф4).
 *
 * ⛔ Возвращает СОСЕДНЕЕ обращение целиком — то, которое надо открыть. Не
 * переключает текущее: вопрос, заданный в Telegram, в Telegram и остаётся.
 */
export async function drugoyMessendzher(
  id: number,
  kind: string,
  channelId?: number,
): Promise<Razbor> {
  await ensureCsrfCookie();

  // ⛔ Номер канала едет вместе с видом (`s614`): у клиники два WhatsApp, и без
  // него подпись обещала бы один номер, а разговор открывался бы в другом.
  const { data } = await apiClient.post<Razbor>(`/obrashcheniya/${id}/drugoy-messendzher`, {
    kind,
    ...(channelId === undefined ? {} : { channel_id: channelId }),
  });

  return data;
}

/** «Объединить» — связать эту клиентку с другой в одного человека (`s607`, Ф3). */
export const obedinit = (id: number, customerId: number) => hod(id, 'obedinit', { customer_id: customerId });

/** «Разъединить» — убрать связь с другой карточкой (`s607`, Ф3). Обратимо. */
export const razedinit = (id: number, customerId: number) => hod(id, 'razedinit', { customer_id: customerId });
